import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button, Card, CardBody, Skeleton } from "@/ui";
import { KarigarHeader } from "@/components/KarigarShell";
import { useAuth } from "@/auth/AuthProvider";
import { scanErrorText, submitScan, type ScanResult } from "@/lib/karigarPoints";
import { useLanguage } from "@/i18n/language";

/**
 * Where a printed sticker lands — `/:orgSlug/c/:code`.
 *
 * The QR on a product holds this URL rather than the bare code, so a karigar
 * can point the camera app they already use at a bottle instead of having to
 * know the app exists, find it, and open its scan tab. That only works if the
 * link does the whole job on arrival: claim the code, say what happened, and
 * get out of the way.
 *
 * Someone arriving here may not be signed in — most will not be, since the
 * sticker is their first contact with the product. They are sent to the
 * karigar sign-in with this page remembered, and the code is claimed as soon
 * as they are back. Nothing is lost by scanning first and signing in second.
 */
export function KarigarClaimPage() {
  const { orgSlug, code } = useParams<{ orgSlug: string; code: string }>();
  const { user, loading, logout } = useAuth();
  // Only a karigar of this org can claim; anyone else is told so instead of posting and failing.
  const canClaim = user?.role === "karigar" && user.orgSlug === orgSlug;
  const navigate = useNavigate();
  const { t } = useLanguage("hi");

  const [result, setResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<{ hi: string; en: string } | null>(null);

  /*
   * Claim exactly once, and never abandon the answer.
   *
   * A code is money, so a second POST is not merely wasteful — it would come
   * back as "already used" and report a perfectly good scan as a failure. The
   * ref guards that. What it must NOT be paired with is a `cancelled` flag in
   * the cleanup: React StrictMode runs this effect twice on the same instance,
   * so the first pass would fire the request, the cleanup would disown its
   * result, and the second pass would skip on the ref — leaving the screen
   * saying "Checking…" for ever, with the points silently credited.
   *
   * The request therefore always applies its result. Setting state on an
   * unmounted component is harmless in React 18 and later, and a claim in
   * flight is worth finishing regardless of what the view is doing.
   */
  const claimed = useRef(false);

  useEffect(() => {
    if (loading || !canClaim || !code || claimed.current) return;
    claimed.current = true;

    submitScan(code)
      .then(setResult)
      .catch((err: unknown) => setError(scanErrorText(err)));
  }, [loading, canClaim, code]);

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface">
        <Skeleton className="h-24 w-64" />
      </div>
    );
  }

  /*
   * Not signed in. `from` carries this exact URL so the sign-in screen returns
   * here and the code is claimed without the karigar having to find the
   * sticker a second time.
   */
  if (!user) {
    return (
      <Navigate
        replace
        state={{ from: `/${orgSlug}/c/${code}` }}
        to={`/${orgSlug}/login/karigar`}
      />
    );
  }

  if (!canClaim) {
    return (
      <div className="flex h-dvh flex-col bg-surface">
        <KarigarHeader title={t("कोड स्कैन", "Code scan")} />
        <div className="relative mx-auto -mt-10 flex w-full max-w-md flex-col gap-4 px-4">
          <Card>
            <CardBody className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-base font-medium text-ink">
                {t("यह कोड कारीगर के खाते से स्कैन होता है।", "This code is claimed from a karigar account.")}
              </p>
              <p className="text-sm text-muted">
                {t(
                  `आप ${user.name} के रूप में साइन इन हैं। कारीगर के रूप में साइन इन करके दोबारा खोलें।`,
                  `You are signed in as ${user.name}. Sign in as a karigar to claim it.`,
                )}
              </p>
              <Button
                onClick={() =>
                  void logout().then(() =>
                    navigate(`/${orgSlug}/login/karigar`, { replace: true, state: { from: `/${orgSlug}/c/${code}` } }),
                  )
                }
              >
                {t("कारीगर के रूप में साइन इन करें", "Sign in as a karigar")}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  const settled = result !== null || error !== null;

  return (
    <div className="flex h-dvh flex-col bg-surface">
      <div className="min-h-0 flex-1 overflow-y-auto pb-8">
        <KarigarHeader title={t("कोड स्कैन", "Code scan")} />
          <div className="relative mx-auto -mt-10 flex max-w-md flex-col gap-4 px-4">
            <Card>
              <CardBody className="flex flex-col items-center gap-2 py-8 text-center">
                {!settled ? (
                  <>
                    <Skeleton className="size-10 rounded-full" />
                    <p className="text-sm text-muted">{t("जाँच हो रही है…", "Checking…")}</p>
                  </>
                ) : result ? (
                  <>
                    <CheckCircle2 aria-hidden="true" className="size-10 text-success" />
                    <div role="status">
                      <p className="font-title text-3xl font-semibold tabular-nums text-ink">
                        +{result.points}
                      </p>
                      <p className="text-sm text-muted">{t("अंक मिले", "points added")}</p>
                      <p className="mt-3 text-base text-ink">
                        {t("कुल अंक", "Total points")}:{" "}
                        <span className="font-semibold tabular-nums">{result.balance}</span>
                      </p>
                    </div>
                  </>
                ) : error ? (
                  <>
                    <XCircle aria-hidden="true" className="size-10 text-danger" />
                    <div role="status">
                      <p className="text-base font-medium text-ink">{t(error.hi, error.en)}</p>
                    </div>
                  </>
                ) : null}
              </CardBody>
            </Card>

            {settled ? (
              <Button block onClick={() => navigate(`/${orgSlug}/karigar`)} size="lg">
                {t("होम पर जाएँ", "Go to home")}
              </Button>
            ) : null}
          </div>
      </div>
    </div>
  );
}
